<template>
  <div class="eticket-page">
    <section class="eticket-filters">
      <t-select
        v-model="filters.distributor"
        clearable
        filterable
        placeholder="选择经销商"
        class="filter-control"
      >
        <t-option
          v-for="item in distributors"
          :key="item.username"
          :label="formatDistributor(item)"
          :value="item.username"
        />
      </t-select>
      <t-date-picker
        v-model="filters.startTime"
        enable-time-picker
        format="YYYY-MM-DD HH:mm:ss"
        value-type="YYYY-MM-DD HH:mm:ss"
        class="filter-control"
        placeholder="开始时间"
      />
      <t-date-picker
        v-model="filters.endTime"
        enable-time-picker
        format="YYYY-MM-DD HH:mm:ss"
        value-type="YYYY-MM-DD HH:mm:ss"
        class="filter-control"
        placeholder="结束时间"
      />
      <t-input-number
        v-model="filters.amount"
        class="filter-control"
        :decimal-places="2"
        :min="0"
        placeholder="指定核算金额"
      />
      <t-button theme="primary" :loading="loading" @click="fetchEtickets">查询</t-button>
      <t-button variant="outline" @click="resetFilters">重置</t-button>
    </section>

    <section class="summary-grid">
      <div class="summary-item">
        <span>生成数量</span>
        <strong>{{ summary.count }}</strong>
      </div>
      <div class="summary-item">
        <span>单价</span>
        <strong>{{ money(summary.unitPrice) }}</strong>
      </div>
      <div class="summary-item">
        <span>应核算金额</span>
        <strong>{{ money(summary.calculatedAmount) }}</strong>
      </div>
      <div class="summary-item">
        <span>金额差额</span>
        <strong :class="{ danger: Number(summary.difference || 0) !== 0 }">
          {{ summary.difference === null ? '-' : money(summary.difference) }}
        </strong>
      </div>
    </section>

    <section class="table-section">
      <div class="section-title">按游戏统计</div>
      <t-table
        row-key="appid"
        :data="gameStats"
        :columns="gameColumns"
        :loading="loading"
        bordered
        stripe
      />
    </section>

    <section class="table-section">
      <div class="section-title">ETicket记录</div>
      <t-table
        row-key="id"
        :data="records"
        :columns="recordColumns"
        :loading="loading"
        bordered
        stripe
      />
      <el-pagination
        v-if="total > 0"
        :current-page="pagination.current"
        :page-size="pagination.pageSize"
        :total="total"
        layout="total, prev, pager, next, jumper"
        @current-change="handlePageChange"
      />
    </section>
  </div>
</template>

<script>
import axios from '../axios';
import { ElMessage } from '@/plugins/tdesignElementAdapter';

const emptySummary = () => ({
  count: 0,
  unitPrice: 18,
  calculatedAmount: 0,
  specifiedAmount: null,
  difference: null,
});

export default {
  name: 'EticketStats',
  data() {
    return {
      loading: false,
      distributors: [],
      filters: {
        distributor: '',
        startTime: '',
        endTime: '',
        amount: null,
      },
      summary: emptySummary(),
      gameStats: [],
      records: [],
      total: 0,
      pagination: {
        current: 1,
        pageSize: 10,
      },
      gameColumns: [
        { colKey: 'appid', title: 'AppID', width: 120 },
        { colKey: 'game_title', title: '游戏', ellipsis: true },
        { colKey: 'total_count', title: '生成数量', width: 120 },
        {
          colKey: 'total_amount',
          title: '核算金额',
          width: 140,
          cell: (h, { row }) => `¥${Number(row.total_amount || 0).toFixed(2)}`,
        },
      ],
      recordColumns: [
        { colKey: 'id', title: 'ID', width: 90 },
        { colKey: 'creator', title: '经销商', width: 130 },
        { colKey: 'username', title: '用户账号', width: 130 },
        { colKey: 'appid', title: 'AppID', width: 110 },
        { colKey: 'game_title', title: '游戏', ellipsis: true },
        { colKey: 'steamid', title: 'SteamID64', width: 180 },
        { colKey: 'unit_price', title: '单价', width: 100, cell: (h, { row }) => `¥${Number(row.unit_price || 0).toFixed(2)}` },
        { colKey: 'create_time', title: '创建时间', width: 180 },
      ],
    };
  },
  created() {
    this.fetchDistributors();
    this.fetchEtickets();
  },
  methods: {
    money(value) {
      const number = Number(value || 0);
      return `¥${number.toFixed(2)}`;
    },
    formatDistributor(item) {
      return item.TaobaoShop ? `${item.username} (${item.TaobaoShop})` : item.username;
    },
    normalizeDateTime(value) {
      return value ? value.replace('T', ' ') + (value.length === 16 ? ':00' : '') : '';
    },
    async fetchDistributors() {
      try {
        const token = localStorage.getItem('user-token');
        const response = await axios.post('/index.php?action=Distributors_list', { token });
        this.distributors = response.data?.distributors || [];
      } catch (error) {
        this.distributors = [];
      }
    },
    async fetchEtickets() {
      this.loading = true;
      try {
        const token = localStorage.getItem('user-token');
        const response = await axios.post('/index.php?action=getEticketStats', {
          token,
          distributor: this.filters.distributor,
          startTime: this.normalizeDateTime(this.filters.startTime),
          endTime: this.normalizeDateTime(this.filters.endTime),
          amount: this.filters.amount,
          page: this.pagination.current,
          size: this.pagination.pageSize,
        });

        if (response.data?.success) {
          this.summary = { ...emptySummary(), ...(response.data.summary || {}) };
          this.gameStats = response.data.gameStats || [];
          this.records = response.data.records || [];
          this.total = Number(response.data.total || 0);
        } else {
          ElMessage.error(response.data?.message || 'ETicket数据加载失败');
        }
      } catch (error) {
        ElMessage.error('ETicket数据加载失败');
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        distributor: '',
        startTime: '',
        endTime: '',
        amount: null,
      };
      this.pagination.current = 1;
      this.fetchEtickets();
    },
    handlePageChange(page) {
      this.pagination.current = page;
      this.fetchEtickets();
    },
  },
};
</script>

<style scoped>
.eticket-page {
  display: grid;
  gap: 18px;
}

.eticket-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.filter-control {
  width: 220px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-item {
  padding: 18px;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  background: #fff;
}

.summary-item span {
  display: block;
  color: #64748b;
  font-size: 13px;
}

.summary-item strong {
  display: block;
  margin-top: 8px;
  color: #111827;
  font-size: 24px;
  line-height: 1.2;
}

.summary-item .danger {
  color: #e34d59;
}

.table-section {
  min-width: 0;
}

.section-title {
  margin-bottom: 10px;
  color: #334155;
  font-size: 15px;
  font-weight: 700;
}

@media (max-width: 900px) {
  .filter-control {
    width: 100%;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
